import { createContext, useContext, type ReactNode } from 'react';
import { ROUTES, type AuthConfigResponse } from '@bioterafi/api-contract';
import type { FetchLike } from '../data/api';
import type { AccountGateway, SignInFlow } from './flow';

/**
 * How this server signs people in (GET /auth/config; AUTH-152), plus the sign-in service's flows when Clerk
 * is active. Pages read it through useAuthRuntime() and never import @clerk/react themselves, so tests pass
 * fakes and builds without Clerk keys never load Clerk (ADR-0021).
 */
export interface AuthConfig extends AuthConfigResponse {
  /** True when /auth/config could not be read; treated as "sign-in not available" (DASH-172). */
  failed: boolean;
}

export const NO_AUTH: AuthConfig = { clerkPublishableKey: null, devSignIn: false, pricingPaused: false, failed: false };

/**
 * The custom sign-in and account flows (DASH-170, DASH-173). Hooks, because the real ones wrap Clerk's hooks;
 * each is called unconditionally by a component that renders only while Clerk is active.
 */
export interface ClerkFlows {
  /** `returnTo` is the sanitised same-origin path to land on afterwards (DASH-063). */
  useSignInFlow: (returnTo: string) => SignInFlow;
  useAccount: () => AccountGateway;
}

export interface AuthRuntime {
  config: AuthConfig;
  /** Present only while Clerk is active (a publishable key was served and Clerk is mounted). */
  clerk: ClerkFlows | null;
}

const AuthRuntimeContext = createContext<AuthRuntime>({ config: NO_AUTH, clerk: null });

export function AuthRuntimeProvider({ value, children }: { value: AuthRuntime; children: ReactNode }) {
  return <AuthRuntimeContext value={value}>{children}</AuthRuntimeContext>;
}

export function useAuthRuntime(): AuthRuntime {
  return useContext(AuthRuntimeContext);
}

/** Reads /auth/config. Any failure means no sign-in method is offered, never a crash (AUTH-162, DASH-172). */
export async function loadAuthConfig(fetcher: FetchLike): Promise<AuthConfig> {
  try {
    const response = await fetcher(ROUTES.authConfig, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) return { ...NO_AUTH, failed: true };
    const body = (await response.json()) as Partial<AuthConfigResponse>;
    const key = typeof body.clerkPublishableKey === 'string' && /^pk_(test|live)_/.test(body.clerkPublishableKey) ? body.clerkPublishableKey : null;
    return { clerkPublishableKey: key, devSignIn: body.devSignIn === true, pricingPaused: body.pricingPaused === true, failed: false };
  } catch {
    return { ...NO_AUTH, failed: true };
  }
}

/** True for a relative path or an absolute URL on this page's origin; the token never leaves the origin. */
function isSameOrigin(input: string): boolean {
  if (input.startsWith('/') && !input.startsWith('//')) return true;
  try {
    return typeof window !== 'undefined' && new URL(input).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * A fetch that adds `Authorization: Bearer <token>` to same-origin requests when getToken returns one
 * (AUTH-166). A header the caller set itself wins. A failing getToken sends the request without a token,
 * so the Worker answers as for a signed-out reader rather than the page failing.
 */
export function withBearerToken(fetcher: FetchLike, getToken: () => Promise<string | null>): FetchLike {
  return async (input, init) => {
    if (!isSameOrigin(input)) return fetcher(input, init);
    let token: string | null = null;
    try {
      token = await getToken();
    } catch {
      token = null;
    }
    if (!token) return fetcher(input, init);
    const headers = new Headers(init?.headers);
    if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    return fetcher(input, { ...init, headers });
  };
}

/**
 * Token source for fetches made outside the session context (the evidence source and the getJson helpers'
 * default transport). The Clerk bridge registers it while Clerk is mounted; otherwise requests go out as they are.
 */
let globalTokenGetter: (() => Promise<string | null>) | null = null;

export function setGlobalTokenGetter(getter: (() => Promise<string | null>) | null): void {
  globalTokenGetter = getter;
}

const plainFetch: FetchLike = (input, init) => fetch(input, init);

/** window.fetch, plus the Clerk token when one is registered. The app's default transport. */
export const appFetch: FetchLike = (input, init) =>
  globalTokenGetter ? withBearerToken(plainFetch, globalTokenGetter)(input, init) : plainFetch(input, init);
